import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';

// LOGO URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_liquid-dignity/artifacts/63xg2u2p_WhatsApp%20Image%202026-03-14%20at%2018.12.05.jpeg";

// Hero Images - Emotional photos
const HERO_IMAGES = [
  "https://customer-assets.emergentagent.com/job_liquid-dignity/artifacts/rk0hr1zl_WhatsApp%20Image%202026-03-14%20at%2018.11.51.jpeg",
  "https://customer-assets.emergentagent.com/job_liquid-dignity/artifacts/8192ga0e_WhatsApp%20Image%202026-03-14%20at%2018.11.47%20%283%29.jpeg",
  "https://customer-assets.emergentagent.com/job_liquid-dignity/artifacts/fb3g3hx7_WhatsApp%20Image%202026-03-14%20at%2018.11.48%20%283%29.jpeg",
];

// UK Flag SVG Component
const UKFlag = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg">
    <clipPath id="s">
      <path d="M0,0 v30 h60 v-30 z"/>
    </clipPath>
    <clipPath id="t">
      <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/>
    </clipPath>
    <g clipPath="url(#s)">
      <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
    </g>
  </svg>
);

// Subtle Rain Drop Component - MUCH SOFTER
const SubtleRain = () => {
  const [drops, setDrops] = useState([]);

  useEffect(() => {
    const newDrops = [];
    for (let i = 0; i < 40; i++) {
      newDrops.push({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 8,
        duration: 4 + Math.random() * 4,
        opacity: 0.1 + Math.random() * 0.15,
        height: 15 + Math.random() * 25,
      });
    }
    setDrops(newDrops);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-[5] overflow-hidden">
      {drops.map((drop) => (
        <motion.div
          key={drop.id}
          className="absolute rounded-full"
          style={{
            left: `${drop.left}%`,
            width: '1px',
            height: `${drop.height}px`,
            background: `linear-gradient(to bottom, transparent, rgba(100, 200, 255, ${drop.opacity}), transparent)`,
          }}
          initial={{ top: '-5%', opacity: 0 }}
          animate={{ 
            top: '105%', 
            opacity: [0, drop.opacity, drop.opacity, 0],
          }}
          transition={{
            duration: drop.duration,
            delay: drop.delay,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      ))}
    </div>
  );
};

// CIRCULAR Logo Component with Glow
const CircularLogo = ({ size = "large" }) => {
  const sizeMap = {
    small: { container: 'w-14 h-14', image: 'w-12 h-12' },
    medium: { container: 'w-20 h-20', image: 'w-18 h-18' },
    large: { container: 'w-32 h-32 md:w-40 md:h-40', image: 'w-28 h-28 md:w-36 md:h-36' },
    hero: { container: 'w-44 h-44 md:w-56 md:h-56 lg:w-64 lg:h-64', image: 'w-40 h-40 md:w-52 md:h-52 lg:w-60 lg:h-60' },
  };

  const sizes = sizeMap[size];

  return (
    <motion.div
      className={`relative ${sizes.container} flex items-center justify-center`}
      animate={{
        boxShadow: [
          '0 0 30px rgba(244, 114, 182, 0.3), 0 0 60px rgba(244, 114, 182, 0.1)',
          '0 0 50px rgba(244, 114, 182, 0.5), 0 0 100px rgba(244, 114, 182, 0.2)',
          '0 0 30px rgba(244, 114, 182, 0.3), 0 0 60px rgba(244, 114, 182, 0.1)',
        ],
      }}
      transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
      style={{ borderRadius: '50%' }}
    >
      {/* Outer glow ring */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-rose-400/20 via-transparent to-emerald-600/20" />
      
      {/* Border ring */}
      <div className="absolute inset-1 rounded-full border-2 border-rose-500/30" />
      
      {/* Inner container */}
      <div className={`${sizes.image} rounded-full overflow-hidden bg-[#0A1F3A] p-1 relative z-10`}>
        <img 
          src={LOGO_URL} 
          alt="HYDRO LIFE Foundation" 
          className="w-full h-full object-cover rounded-full"
        />
      </div>
    </motion.div>
  );
};

// Main Hero Section with Parallax
export const HeroSection = ({ language }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const { scrollY } = useScroll();
  
  // Parallax transforms
  const backgroundY = useTransform(scrollY, [0, 500], [0, 150]);
  const contentY = useTransform(scrollY, [0, 500], [0, -50]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0.3]);

  // Auto-rotate hero images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const content = {
    en: {
      tagline: 'BRITISH TRUST • GLOBAL IMPACT',
      headline: 'Every Drop',
      highlight: 'Saves a Life',
      subheadline: 'Join the movement bringing clean water to communities worldwide.',
      cta: 'Donate Now',
      secondary: 'Our Mission',
    },
    es: {
      tagline: 'CONFIANZA BRITÁNICA • IMPACTO GLOBAL',
      headline: 'Cada Gota',
      highlight: 'Salva una Vida',
      subheadline: 'Únete al movimiento que lleva agua limpia a comunidades en todo el mundo.',
      cta: 'Dona Ahora',
      secondary: 'Nuestra Misión',
    },
  };

  const t = content[language];

  const scrollToSection = (id) => {
    // For donate, scroll to the donorbox form directly
    const targetId = id === 'donate' ? 'donorbox-form' : id;
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section 
      className="relative min-h-screen overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax Background Image */}
      <motion.div 
        className="absolute inset-0 z-0"
        style={{ y: backgroundY }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImage}
            className="absolute inset-0"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: 'easeOut' }}
          >
            <img
              src={HERO_IMAGES[currentImage]}
              alt="HYDRO LIFE Impact"
              className="w-full h-[120%] object-cover object-center"
            />
          </motion.div>
        </AnimatePresence>
        
        {/* Smooth gradient overlays - NO HARSH CUTS */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F3A]/80 via-[#0A1F3A]/40 to-[#0A1F3A]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F3A]/70 via-transparent to-[#0A1F3A]/30" />
      </motion.div>

      {/* Content with Parallax */}
      <motion.div 
        className="relative z-10 max-w-7xl mx-auto px-6 min-h-screen flex items-center"
        style={{ y: contentY, opacity }}
      >
        <div className="w-full py-32">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12 lg:gap-16">
            
            {/* Left: CIRCULAR Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="flex flex-col items-center"
            >
              <CircularLogo size="hero" />
              
              {/* UK Badge below logo */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mt-6 flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20"
              >
                <UKFlag className="w-6 h-4" />
                <span className="text-xs font-medium text-amber-200/90 tracking-wider">
                  {t.tagline}
                </span>
              </motion.div>
            </motion.div>

            {/* Right: Text Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex-1 text-center lg:text-left"
            >
              {/* Elegant Typography */}
              <h1 className="mb-6">
                <span className="block text-4xl sm:text-5xl lg:text-6xl font-light text-white/90 tracking-tight">
                  {t.headline}
                </span>
                <span className="block text-4xl sm:text-5xl lg:text-6xl font-bold mt-2 bg-gradient-to-r from-rose-300 via-pink-200 to-cyan-300 bg-clip-text text-transparent">
                  {t.highlight}
                </span>
              </h1>

              <p className="text-lg text-white/70 mb-10 max-w-md mx-auto lg:mx-0 font-light leading-relaxed">
                {t.subheadline}
              </p>

              {/* CTAs - Rose/Coral gradient */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <motion.button
                  onClick={() => scrollToSection('donate')}
                  className="group px-8 py-4 rounded-full bg-gradient-to-r from-rose-500 via-pink-500 to-cyan-500 text-white font-semibold text-lg shadow-lg shadow-rose-500/30 hover:shadow-rose-500/50 transition-all"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="hero-cta"
                >
                  <span className="flex items-center gap-2">
                    {t.cta}
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                </motion.button>

                <motion.button
                  onClick={() => scrollToSection('mission')}
                  className="px-8 py-4 rounded-full border border-white/30 text-white/90 font-medium hover:bg-white/10 hover:border-white/50 transition-all"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {t.secondary}
                </motion.button>
              </div>

              {/* Trust Indicators */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="mt-10 flex flex-wrap items-center gap-6 justify-center lg:justify-start text-white/50 text-sm"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span>100% Transparent</span>
                </div>
                <div className="flex items-center gap-2">
                  <UKFlag className="w-5 h-3 opacity-70" />
                  <span>UK Registered</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          className="w-6 h-10 rounded-full border border-white/30 flex justify-center pt-2"
        >
          <div className="w-1 h-2 rounded-full bg-amber-400/70" />
        </motion.div>
      </motion.div>

      {/* Image Dots */}
      <div className="absolute bottom-8 right-8 z-20 flex gap-2">
        {HERO_IMAGES.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImage(index)}
            className={`h-1.5 rounded-full transition-all ${
              currentImage === index 
                ? 'bg-amber-400 w-6' 
                : 'bg-white/30 w-1.5 hover:bg-white/50'
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSection;
export { CircularLogo, UKFlag, SubtleRain };
